import { useState, useCallback, useEffect } from "react";
import DataTable from '../DataTable/DataTable';
import { NotificationManager } from "react-notifications";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { convertDate } from "../AcitivityModal/assistingFunctions";
import activityReport from "../../apis/activityReport/activityReport";
import "./requisitionReports.css";
import config from "../../Config.json";
const stylesSX = {
  odd: {
    background: '#F6F6F6 !important',
  },
  even: {
    background: '#ffffff !important',
  },
  backdrop: (theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }),
  waitingText: {
    marginLeft: '5px',
  },
};

const ActivityReport = (props) => {
    const {
        userType,
        userIdType,
        userRecruitmentpartnerId,
        startSelectedDate,
        endSelectedDate,
        exports,
        additionalFilter,
        setTriggerActivity
    } = props;

    const [takeNumberOfResult, setTakeNumberOfResult] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [dataResult, setDataResult] = useState([]);
    const [totalResult, setTotalResult] = useState(0);
    const [isSearching, setIsSearching] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [prevStartDate, setPrevStartDate] = useState(null);
    const [prevEndDate, setPrevEndDate] = useState(null);
    const [prevFilter, setPrevFilter] = useState({});

    // Define columns for the DataTable
    const columns = [
        {
            accessorKey: "displayName",
            header: "Name",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue, row }) => {
                return row.original.uploadType === "Batch Upload" ? "Batch Upload" : getValue() || "";
            },
        },
        {
            accessorKey: "email",
            header: "Email",
            width: "300px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue, row }) => {
                return row.original.uploadType === "Batch Upload" ? "Batch Upload" : getValue() || "";
            },
        },
        {
            accessorKey: "documentCount",
            header: "Candidate Submitted",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "duration",
            header: "Duration",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "createdAt",
            header: "Date",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const rawDate = getValue();
                if (!rawDate) return "";
                const date = new Date(rawDate);
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
        },
        {
            accessorKey: "uploadType",
            header: "Uploaded Type",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
    ];

    // Search function without debounce
    const performSearch = useCallback(async (
        userRecruitmentpartnerId,
        pageNumber,
        takeNumberOfResult,
        startSelectedDate,
        endSelectedDate,
        userId,
        additionalFilter
    ) => {
        try {
            setIsSearching(true);
            setDataResult([]);

            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const payload = {
                userRecruitmentpartnerId,
                page: pageNumber,
                size: takeNumberOfResult,
                formattedDatestart: convertDate(startSelectedDate),
                formattedDateend: convertDate(endSelectedDate),
                isExport: false,
                userId: userId,
                role: userType,
                timezone,
                additionalFilter
            };

            const search = await activityReport(payload);

            if (search?.totalCount?.[0]?.total >= 0) {
                const total = search.totalCount[0].total;
                const data = search.data;
                setDataResult(data);
                setTotalResult(total);
            } else {
                setDataResult([]);
                setTotalResult(0);
            }
            setIsSearching(false);
        } catch (error) {
            console.error("Error fetching activity report:", error);
            setDataResult([]);
            setTotalResult(0);
            setIsSearching(false);
            NotificationManager.error("Failed to load data", "Error", 10000);
        }
    }, [userType]);

    // Handle page change
    const handlePageChange = (newPage, newPageSize) => {
        setPageNumber(newPage);
        if (newPageSize && newPageSize !== takeNumberOfResult) {
            setTakeNumberOfResult(newPageSize);
        }
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        setTakeNumberOfResult(newPageSize);
        setPageNumber(1);
    };

    

    // Reset page if filter changes
    useEffect(() => {
        const datesChanged = prevStartDate !== startSelectedDate || prevEndDate !== endSelectedDate;
        const filtersChanged = JSON.stringify(prevFilter) !== JSON.stringify(additionalFilter);

        if (datesChanged || filtersChanged) {
            setPageNumber(1);
            setPrevStartDate(startSelectedDate);
            setPrevEndDate(endSelectedDate);
            setPrevFilter({ ...additionalFilter });
        }
    }, [additionalFilter, startSelectedDate, endSelectedDate, prevStartDate, prevEndDate, prevFilter]);

    useEffect(() => {
        const startDate = convertDate(startSelectedDate);
        const endDate = convertDate(endSelectedDate);

        if (!startSelectedDate || !endSelectedDate || startDate > endDate) {
            setDataResult([]);
            setTotalResult(0);
            NotificationManager.error("Please enter a valid date", "Error", 10000);
        } else {
            // When any dependency changes, schedule a search.
            setIsSearching(true);
            setDataResult([]);

            const timeoutId = setTimeout(() => {
                performSearch(
                    userRecruitmentpartnerId,
                    pageNumber,
                    takeNumberOfResult,
                    startSelectedDate,
                    endSelectedDate,
                    userIdType,
                    additionalFilter
                );
            }, 1000); // 1000ms debounce delay

            // Cleanup cancels the pending search if dependencies change quickly.
            return () => clearTimeout(timeoutId);
        }
    }, [pageNumber, takeNumberOfResult, startSelectedDate, endSelectedDate, additionalFilter, userRecruitmentpartnerId, userIdType, performSearch]);

    const callApiToGenerateExcel = useCallback(async () => {
        setIsExporting(true);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const startDate = convertDate(startSelectedDate);
        const endDate = convertDate(endSelectedDate);

        let exportArr = [];
        let maxSize = config.MAX_SIZE ;
        let end = Math.ceil(totalResult / maxSize);
        let pageStart = 1;
        try {
            for (let start = 0; start < end; start++) {
                const payload = {
                    userRecruitmentpartnerId,
                    page: pageStart,
                    size: maxSize,
                    formattedDatestart: startDate,
                    formattedDateend: endDate,
                    isExport: true,
                    userId: userIdType,
                    role: userType,
                    additionalFilter,
                    timezone
                };
                const search = await activityReport(payload);

                search?.data.forEach((ele) => {
                    const newObj = {
                        "Name": ele.uploadType === "Batch Upload" ? "Batch Upload" : ele.displayName || "",
                        "Email": ele.uploadType === "Batch Upload" ? "Batch Upload" : ele.email || "",
                        "Candidate Submitted": ele.documentCount || "",
                        "Duration": ele.duration || "",
                        "Date": ele.createdAt || "",
                        "Uploaded Type": ele.uploadType || ""
                    };
                    exportArr.push(newObj);
                });
                pageStart = pageStart + maxSize;
            }

            if (exportArr.length === 0) {
                NotificationManager.error("No data available", "Error", 10000);
                setTriggerActivity(false);
                setIsExporting(false);
                return;
            }
            // Lazy load XLSX only when needed for export
            const { utils, writeFile } = await import("xlsx");
            const ws = utils.json_to_sheet(exportArr);
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, "Activity Report");
            writeFile(wb, "Activity_report.xlsx");

            setTriggerActivity(false);
            setIsExporting(false);
        } catch (error) {
            console.error("Error exporting:", error);
            NotificationManager.error("Failed to export", "Error", 10000);
            setIsExporting(false);
        }
    }, [totalResult, userRecruitmentpartnerId, startSelectedDate, endSelectedDate, userIdType, userType, additionalFilter, setTriggerActivity]);

    useEffect(() => {
        if (exports) {
            callApiToGenerateExcel();
        }
    }, [exports, callApiToGenerateExcel]);

    return (
        <>
            <Backdrop sx={stylesSX.backdrop} open={isExporting}>
                <CircularProgress color="inherit" />
                <Typography sx={stylesSX.waitingText}>Please wait...</Typography>
            </Backdrop>

            <DataTable
                data={dataResult}
                columns={columns}
                isLoading={isSearching}
                totalRecords={totalResult}
                currentPage={pageNumber}
                pageSize={takeNumberOfResult}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                
                enableFiltering={false} 
                enablePagination={true}
                enableSorting={false} 
                pageSizeOptions={[5, 10, 20, 50, 100]}
                isServerSide={true}
                className="kendo_reports"
                noDataText="No results found !"
            />
        </>
    );
};

export default ActivityReport;
